import { useRef, useCallback, useEffect } from 'react';
import { Box, useTheme } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Scrollbar } from 'swiper';
import PlateBox from './PlateBox';

import 'swiper/css';
import 'swiper/css/scrollbar';

const AUTOPLAY_PX_PER_SEC = 60;
const AUTOPLAY_EDGE_WAIT = 3000;
const AUTOPLAY_RESUME_DELAY = 2000;

const PlatesSlider = ({ filterPlates, typePlate }) => {
  const theme = useTheme();
  const swiperRef = useRef(null);

  // Autoplay: ping-pong scroll at a constant speed, independent of slide count
  const autoplayDirection = useRef('forward'); // 'forward' -> end, 'backward' -> start
  const autoplayPaused = useRef(false); // true while the mouse is over the slider
  const moveTimeoutRef = useRef(null);
  const waitTimeoutRef = useRef(null);
  const resumeTimeoutRef = useRef(null);

  const clearAutoplayTimers = useCallback(() => {
    clearTimeout(moveTimeoutRef.current);
    clearTimeout(waitTimeoutRef.current);
    clearTimeout(resumeTimeoutRef.current);
  }, []);

  // Glide toward the current direction's edge at AUTOPLAY_PX_PER_SEC, then
  // wait at the edge before scheduleAutoplayReverse() flips the direction.
  const runAutoplayStep = useCallback(() => {
    if (autoplayPaused.current) return;
    const swiper = swiperRef.current;
    if (!swiper) return;

    const min = swiper.minTranslate();
    const max = swiper.maxTranslate();
    if (min === max) return; // nothing to scroll

    const target = autoplayDirection.current === 'forward' ? max : min;
    const current = swiper.getTranslate();
    const remaining = Math.abs(target - current);

    if (remaining < 1) {
      scheduleAutoplayReverse();
      return;
    }

    const duration = (remaining / AUTOPLAY_PX_PER_SEC) * 1000;
    swiper.setTransition(duration);
    swiper.setTranslate(target);
    swiper.updateProgress(target);

    moveTimeoutRef.current = setTimeout(() => {
      if (autoplayPaused.current) return;
      scheduleAutoplayReverse();
    }, duration);
  }, []);

  function scheduleAutoplayReverse() {
    waitTimeoutRef.current = setTimeout(() => {
      if (autoplayPaused.current) return;
      autoplayDirection.current =
        autoplayDirection.current === 'forward' ? 'backward' : 'forward';
      runAutoplayStep();
    }, AUTOPLAY_EDGE_WAIT);
  }

  // Mouse enters the slider: freeze in place immediately, cancel any pending timers
  const handleAutoplayPause = useCallback(() => {
    autoplayPaused.current = true;
    clearAutoplayTimers();
    const swiper = swiperRef.current;
    if (!swiper) return;
    const current = swiper.getTranslate();
    swiper.setTransition(0);
    swiper.setTranslate(current);
    swiper.updateProgress(current);
  }, [clearAutoplayTimers]);

  // Mouse leaves: resume the ping-pong movement after AUTOPLAY_RESUME_DELAY
  const handleAutoplayResume = useCallback(() => {
    clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      autoplayPaused.current = false;
      runAutoplayStep();
    }, AUTOPLAY_RESUME_DELAY);
  }, [runAutoplayStep]);

  useEffect(() => clearAutoplayTimers, [clearAutoplayTimers]);

  if (!filterPlates?.length) return null;

  return (
    <Box
      onMouseEnter={handleAutoplayPause}
      onMouseLeave={handleAutoplayResume}
      sx={{
        position: 'relative',
        width: '100%',
        mb: '20px',
        '& .swiper-scrollbar': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          height: '6px',
          borderRadius: '3px',
          cursor: 'pointer',
          transition: 'height 0.2s ease',
          '&:hover': { height: '12px' },
        },
        '& .swiper-scrollbar-drag': {
          backgroundColor: theme.primary,
          borderRadius: '3px',
        },
      }}
    >
      <Swiper
        modules={[Autoplay, Scrollbar]}
        slidesPerView="auto"
        spaceBetween={15}
        scrollbar={{ draggable: true, hide: false }}
        breakpoints={{
          600: { spaceBetween: 16 },
          768: { spaceBetween: 20 },
          1280: { spaceBetween: 24 },
          1580: { spaceBetween: 24 },
        }}
        style={{ paddingBottom: '24px' }}
        onSwiper={swiper => {
          swiperRef.current = swiper;
          runAutoplayStep();
        }}
      >
        {filterPlates.map(placa => (
          <SwiperSlide key={placa.id} style={{ width: 'auto' }}>
            <PlateBox data={placa} typePlate={typePlate} />
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};

export default PlatesSlider;
